import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSharedCard, useImportCard } from '../hooks/useShared'
import { useDecks, useCreateDeck } from '../hooks/useDecks'
import { useAuthStore, selectIsAuthenticated } from '../stores/authStore'
import { useLocale } from '../i18n/context'
import type { CardSide, Deck } from '../types'
import { findTermSide, isTermSide } from '../utils/cardSides'
import { valueSide } from '../utils/fieldValue'

const IMAGE_LABELS = new Set(['Image', 'Image URL'])
const AUDIO_LABELS = new Set(['Audio', 'Audio URL'])
const IMAGE_URL_RE = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|avif)(\?.*)?$/i
const AUDIO_URL_RE = /^https?:\/\/.+\.(mp3|m4a|ogg|wav|webm|aac|opus|flac)(\?.*)?$/i

function isImageSide(side: CardSide): boolean {
  return (
    side.kind === 'image' ||
    IMAGE_LABELS.has(side.label) ||
    side.content.startsWith('data:image/') ||
    IMAGE_URL_RE.test(side.content.trim())
  )
}

function isAudioSide(side: CardSide): boolean {
  return (
    side.kind === 'audio' ||
    AUDIO_LABELS.has(side.label) ||
    side.content.startsWith('data:audio/') ||
    AUDIO_URL_RE.test(side.content.trim())
  )
}

function renderSideContent(side: CardSide) {
  if (isImageSide(side)) {
    return (
      <img
        src={side.content}
        alt=""
        style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, objectFit: 'contain', display: 'block' }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  if (isAudioSide(side)) {
    return (
      <audio
        src={side.content}
        controls
        preload="metadata"
        style={{ width: '100%', maxWidth: 360 }}
        onClick={(e) => e.stopPropagation()}
      />
    )
  }
  return <p style={{ margin: 0, fontSize: 15, fontFamily: 'var(--font-content)', color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{side.content}</p>
}

// ── Deck picker option ────────────────────────────────────────────────────────

function DeckOption({ label, secondary, selected, onClick, bold = false }: {
  label: string; secondary?: string; selected: boolean; onClick: () => void; bold?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`, background: selected ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, fontWeight: bold ? 600 : 400, width: '100%' }}
    >
      <span>{label}</span>
      {secondary && <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0, marginLeft: 8 }}>{secondary}</span>}
    </button>
  )
}

// ── Import card modal ─────────────────────────────────────────────────────────

function ImportCardModal({ shareToken, onClose, onSuccess }: {
  shareToken: string
  onClose: () => void
  onSuccess: (deck: { id: string; title: string }) => void
}) {
  const { tr } = useLocale()
  const sp = tr.sharedPages
  const [selectedId, setSelectedId] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [search, setSearch] = useState('')

  // page_size capped at 100 by the backend (PageParams le=100); covers most users.
  // If a user could own >100 eligible decks, server-side search would be needed.
  const { data: decksData, isLoading: decksLoading, isError: decksError } = useDecks(1, 100)
  const createDeck = useCreateDeck()
  const importCard = useImportCard()

  const allDecks: Deck[] = decksData?.items ?? []
  const filterable = allDecks.filter((d) => !d.is_system)
  const decks = search
    ? filterable.filter((d) => d.title.toLowerCase().includes(search.toLowerCase()))
    : filterable

  const isNew = selectedId === '__new__'
  const canConfirm = isNew ? newTitle.trim().length > 0 : selectedId !== ''
  const isPending = createDeck.isPending || importCard.isPending
  const hasError = createDeck.isError || importCard.isError

  async function handleConfirm() {
    try {
      let targetId = selectedId
      let targetTitle = allDecks.find((d) => d.id === selectedId)?.title ?? ''
      if (isNew) {
        const deck = await createDeck.mutateAsync({ title: newTitle.trim() })
        targetId = deck.id
        targetTitle = deck.title
      }
      await importCard.mutateAsync({ shareToken, targetDeckId: targetId })
      onSuccess({ id: targetId, title: targetTitle || newTitle.trim() })
    } catch {
      // hasError surfaces the error above
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', padding: 28, maxWidth: 440, width: '100%' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{sp.addToDeck}</h3>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={sp.searchDecksPlaceholder}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 14, marginBottom: 10, outline: 'none', display: 'block' }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
          {!search && (
            <>
              <DeckOption label={sp.newDeckOption} selected={isNew} onClick={() => setSelectedId('__new__')} bold />
              {isNew && (
                <input
                  autoFocus
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm && !isPending) handleConfirm() }}
                  placeholder={sp.newDeckPlaceholder}
                  style={{ marginLeft: 16, padding: '7px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--accent)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' }}
                />
              )}
            </>
          )}

          {decks.map((d) => (
            <DeckOption
              key={d.id}
              label={d.title}
              secondary={sp.cardCount(d.card_count)}
              selected={selectedId === d.id}
              onClick={() => setSelectedId(d.id)}
            />
          ))}

          {decksError && (
            <p style={{ fontSize: 13, color: 'var(--danger)', padding: '8px 0', textAlign: 'center', margin: 0 }}>{sp.loadDecksError}</p>
          )}
          {!decksLoading && !decksError && decks.length === 0 && search && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0', textAlign: 'center', margin: 0 }}>{sp.noDecks}</p>
          )}
          {!decksLoading && !decksError && filterable.length === 0 && !search && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 14px', margin: 0 }}>{sp.noDecksYet}</p>
          )}
        </div>

        {hasError && (
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: '0 0 12px' }}>{sp.addError}</p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={isPending} style={{ padding: '9px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{sp.cancelBtn}</button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isPending}
            style={{ padding: '9px 18px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: canConfirm && !isPending ? 'pointer' : 'not-allowed', opacity: !canConfirm || isPending ? 0.6 : 1 }}
          >
            {isPending ? sp.addingBtn : sp.addBtn}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SharedCardPage() {
  const { tr } = useLocale()
  const sp = tr.sharedPages
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(selectIsAuthenticated)

  const { data: card, isLoading, isError } = useSharedCard(token!)
  const [showImportPicker, setShowImportPicker] = useState(false)
  const [importedDeck, setImportedDeck] = useState<{ id: string; title: string } | null>(null)

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
        <div style={{ background: 'var(--bg-header)', height: 56 }} />
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 24px' }}>
          <div style={{ height: 120, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    )
  }

  if (isError || !card) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{sp.cardNotFound}</h2>
          <p style={{ margin: 0, fontSize: 14 }}>{sp.linkExpired}</p>
        </div>
      </div>
    )
  }

  const term = findTermSide(card.sides)?.content ?? ''
  // ADR-104: the card's definition is its first non-blank Definition.
  const definition = valueSide(card.sides, 'definition')?.content ?? ''
  const extraSides = card.sides
    .filter((s) => !isTermSide(s) && s.label !== 'Definition')
    .sort((a, b) => a.position - b.position)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Top bar */}
      <div style={{ background: 'var(--bg-header)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>{'CardSmith' /* i18n-exempt: brand name */}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAuthenticated ? (
            <button onClick={() => navigate('/home')} style={{ padding: '8px 16px', background: 'transparent', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>{sp.myDecks}</button>
          ) : (
            <>
              <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>{sp.logIn}</button>
              <button onClick={() => navigate('/register')} style={{ padding: '8px 16px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{sp.signUpFree}</button>
            </>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 24px' }}>
        {/* Flashcard */}
        <div style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-flashcard)', padding: '40px 48px',
          marginBottom: 24, position: 'relative', overflow: 'hidden',
        }}>
          {/* Corner brackets */}
          {[{ top: 12, left: 12 }, { top: 12, right: 12 }, { bottom: 12, left: 12 }, { bottom: 12, right: 12 }].map((pos, i) => (
            <div key={i} style={{ position: 'absolute', width: 16, height: 16, ...pos, borderTop: i < 2 ? '2px solid var(--accent)' : 'none', borderBottom: i >= 2 ? '2px solid var(--accent)' : 'none', borderLeft: i % 2 === 0 ? '2px solid var(--accent)' : 'none', borderRight: i % 2 === 1 ? '2px solid var(--accent)' : 'none', opacity: 0.4 }} />
          ))}

          <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{sp.termLabel}</p>
          <p style={{ margin: '0 0 28px', fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-content)', color: 'var(--text-primary)', lineHeight: 1.25 }}>{term}</p>

          <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{sp.definitionLabel}</p>
          <p style={{ margin: 0, fontSize: 18, fontFamily: 'var(--font-content)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{definition}</p>

          {extraSides.map((side) => (
            <div key={side.id} style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
              <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{side.label}</p>
              {renderSideContent(side)}
            </div>
          ))}
        </div>

        {/* CTA banner — authenticated: add to deck; unauthenticated: sign-up prompt */}
        {isAuthenticated ? (
          importedDeck ? (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', boxShadow: 'var(--shadow-card)' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.cardAdded(importedDeck.title)}</p>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{sp.cardAddedDesc}</p>
              </div>
              <button onClick={() => navigate(`/decks/${importedDeck.id}`)} style={{ padding: '10px 20px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                {sp.viewDeckArrow}
              </button>
            </div>
          ) : (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', boxShadow: 'var(--shadow-card)' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.saveCardTitle}</p>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{sp.saveCardDesc}</p>
              </div>
              <button onClick={() => setShowImportPicker(true)} style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                {sp.addBtn}
              </button>
            </div>
          )
        ) : (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', boxShadow: 'var(--shadow-card)' }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{sp.learnWithSrTitle}</p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{sp.learnWithSrDesc}</p>
            </div>
            <button onClick={() => navigate('/register')} style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {sp.signUpFreeArrow}
            </button>
          </div>
        )}
      </div>

      {showImportPicker && card.share_token && (
        <ImportCardModal
          shareToken={card.share_token}
          onClose={() => setShowImportPicker(false)}
          onSuccess={(deck) => {
            setShowImportPicker(false)
            setImportedDeck(deck)
          }}
        />
      )}
    </div>
  )
}
